import type { NavigateFunction } from 'react-router-dom'

import { UserRoundPlusIcon } from '@buffer-mono/popcorn'

import { MODALS, openGlobalModal } from '~publish/hooks/useOpenGlobalModal'

import { PRIORITIES } from '../constants'
import { createAction } from './createAction'
import type { CommandBarAction } from './types'

/**
 * Options for building team-related actions.
 */
interface BuildTeamActionsOptions {
  /** React Router navigate function for navigation */
  navigate: NavigateFunction
  /** Whether the current user is on a free plan */
  isFreeUser: boolean
}

/**
 * Builds team-related command bar actions.
 * The "Invite team member" action is visible to all users:
 * - Free users see the upgrade modal
 * - Paid users navigate to the team settings page
 *
 * @param options - Configuration options
 * @param options.navigate - React Router navigate function
 * @param options.isFreeUser - Whether the current user is on a free plan
 * @returns Array of team-related command bar actions
 */
export function buildTeamActions({
  navigate,
  isFreeUser,
}: BuildTeamActionsOptions): CommandBarAction[] {
  return [
    createAction({
      id: 'invite-team-member',
      name: 'Invite Team Member',
      keywords: [
        'invite',
        'team',
        'member',
        'add',
        'user',
        'collaborate',
        'collaboration',
        'share',
      ],
      icon: <UserRoundPlusIcon />,
      section: 'quick-actions',
      priority: PRIORITIES.teamInvite,
      description: 'Add people to your team',
      perform: () => {
        if (isFreeUser) {
          openGlobalModal(MODALS.planSelector, {
            cta: 'publish-commandBar-inviteTeam-1',
            upgradePathName: 'inviteTeam-upgrade',
          })
        } else {
          navigate('/settings/team')
        }
      },
    }),
  ]
}
